// Collection "All colors" view: swaps each multi-color product card for one card per color, using the
// cards pre-rendered in a <template data-color-cards> inside that product's column (see
// snippets/card-product-colors.liquid). Off by default; once turned on it stays on for the rest of the
// visit (sessionStorage), so it carries across paging, sorting, filtering and other collections.
(() => {
  const STORAGE_KEY = 'gs:collection-color-view';
  const TOGGLE_SELECTOR = '[data-color-view-toggle]';

  const isOn = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const remember = (on) => {
    try {
      if (on) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Storage unavailable (e.g. private mode): the view still works for this page.
    }
  };

  const getList = () => document.getElementById('ProductsList');

  // Original product columns that have color cards to show.
  const getExpandableColumns = (list) =>
    [...list.querySelectorAll(':scope > .product-column:not([data-color-card])')].filter((column) => {
      const template = column.querySelector(':scope > template[data-color-cards]');
      return template && template.content.children.length > 0;
    });

  const removeColorCardsAfter = (column) => {
    let next = column.nextElementSibling;
    while (next && next.hasAttribute('data-color-card')) {
      const following = next.nextElementSibling;
      next.remove();
      next = following;
    }
  };

  const render = (on) => {
    const list = getList();
    if (!list) return;

    getExpandableColumns(list).forEach((column) => {
      const expanded = column.hasAttribute('data-colors-expanded');
      if (on && !expanded) {
        const template = column.querySelector(':scope > template[data-color-cards]');
        column.after(template.content.cloneNode(true));
        column.setAttribute('data-colors-expanded', '');
        column.hidden = true;
      } else if (!on && expanded) {
        removeColorCardsAfter(column);
        column.removeAttribute('data-colors-expanded');
        column.hidden = false;
      }
    });

    list.classList.toggle('products-list--colors', on);
    syncToggles(on);
  };

  const syncToggles = (on) => {
    const list = getList();
    const available = Boolean(list && getExpandableColumns(list).length);
    document.querySelectorAll(TOGGLE_SELECTOR).forEach((button) => {
      button.hidden = !available;
      button.setAttribute('aria-pressed', String(on && available));
      const label = button.querySelector('[data-color-view-label]');
      if (label) label.textContent = on && available ? label.dataset.on : label.dataset.off;
    });
  };

  // Keep the shopper looking at the same product when the grid grows or shrinks above them.
  const findAnchor = (list) => {
    const toolbar = document.querySelector('.collection__toolbar');
    const viewTop = toolbar ? toolbar.getBoundingClientRect().bottom : 0;
    return [...list.children].find((el) => !el.hidden && el.getBoundingClientRect().bottom > viewTop);
  };

  const mapAnchor = (anchor, on) => {
    if (!anchor) return null;
    if (on && anchor.hasAttribute('data-colors-expanded')) return anchor.nextElementSibling;
    if (!on && anchor.hasAttribute('data-color-card')) {
      let previous = anchor.previousElementSibling;
      while (previous && previous.hasAttribute('data-color-card')) previous = previous.previousElementSibling;
      return previous;
    }
    return anchor;
  };

  const setView = (on) => {
    const list = getList();
    if (!list) return;
    const listTop = list.getBoundingClientRect().top;
    const anchor = listTop < 0 ? findAnchor(list) : null;
    const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;

    // The browser's own scroll anchoring would also try to compensate for the cards added/removed above
    // the viewport and fight our correction, so switch it off for the swap.
    const root = document.documentElement;
    root.style.overflowAnchor = 'none';

    // Turning off removes the color cards, so find the product they belong to first; turning on, the
    // product's first color card only exists after rendering.
    let target = on ? null : mapAnchor(anchor, on);
    remember(on);
    render(on);
    if (on) target = mapAnchor(anchor, on);

    if (target && target.isConnected) {
      // Instant, not smooth: the grid just changed under the shopper; the jump should be invisible.
      window.scrollBy({ top: target.getBoundingClientRect().top - anchorTop, behavior: 'instant' });
    }
    requestAnimationFrame(() => root.style.removeProperty('overflow-anchor'));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(TOGGLE_SELECTOR);
    if (!button) return;
    setView(button.getAttribute('aria-pressed') !== 'true');
  });

  // Filtering and sorting replace the grid's contents (facets.js); re-apply the current view to the new
  // cards. Our own inserts trigger this too, but a second render finds nothing left to change.
  let pending = false;
  const observer = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      render(isOn());
    });
  });

  const init = () => {
    const wrapper = document.getElementById('ProductGridWrapper');
    if (wrapper) observer.observe(wrapper, { childList: true, subtree: true });
    render(isOn());
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
